import { errorCode, errorMessage } from '@wheere-front/shared-util';

/**
 * Lot de trackers `POST /trackers/batch/site` (api-tracker 6.11.0, flux 12) : MOVE, DETACH,
 * TRANSFER en un seul appel serveur. Règles pures partagées par Manage et la fiche site ; l'appel
 * lui-même vit dans `TrackerBatchService`, le récapitulatif dans `TrackerBatchRunComponent`.
 */
/** UNASSIGN (api-tracker 8.3.0) : retirer de toute organisation, mêmes règles que TRANSFER. */
export type TrackerBatchAction = 'MOVE' | 'DETACH' | 'TRANSFER' | 'UNASSIGN';

/** Limite du contrat (`trackerIds` : 1 à 200). Au-delà, le lot part en plusieurs appels. */
export const TRACKER_BATCH_MAX = 200;

/** Codes d'échec par tracker du contrat (`TrackerBatchFailure.code`). */
export const TRACKER_BATCH_CODES = [
  'TRACKER_NOT_FOUND',
  'ACCESS_DENIED',
  'SITE_NOT_FOUND',
  'TRACKER_HAS_NO_ORGANIZATION',
  'SITE_HAS_NO_ORGANIZATION',
  'ORGANIZATION_MISMATCH',
  'LORA_SLOTS_FULL',
  'LORA_PROFILE_NOT_ON_SITE',
  'PROFILE_CHANGE_REQUIRES_REPROGRAMMATION',
  'TRACKER_LOGGING_ACTIVE',
  'LOGGER_SERVICE_UNAVAILABLE',
  'TRACKER_ON_OFFLINE_SITE',
  'TRACKER_ON_SITE',
  'LORA_TOPOLOGY_NOT_PUBLISHED',
  'MQTT_PUBLISH_FAILED',
  'INTERNAL_ERROR',
] as const;
export type TrackerBatchCode = (typeof TRACKER_BATCH_CODES)[number];

export interface TrackerBatchFailureItem {
  trackerId: string;
  code: string;
  message: string;
}

export interface TrackerBatchOutcome {
  succeeded: string[];
  failed: TrackerBatchFailureItem[];
}

/** Ce que l'appelant fixe pour un lot ; `trackerIds` et `stopLogging` sont ajoutés à l'envoi. */
export interface TrackerBatchParams {
  action: TrackerBatchAction;
  siteId?: string;
  profileId?: string;
  organizationId?: string;
}

export interface TrackerBatchCall extends TrackerBatchParams {
  trackerIds: string[];
  stopLogging: boolean;
}

/** Envoie un lot dont l'action est déjà fixée : seuls les ids et le choix « arrêter les logs » varient. */
export type BatchExecutor = (ids: string[], stopLogging: boolean) => Promise<TrackerBatchOutcome>;

/** En cours de log = `Tracker.logSessionStarted` renseigné (même lecture que l'enregistreur de logs). */
export function isTrackerLogging(t: { logSessionStarted?: unknown }): boolean {
  return t.logSessionStarted !== null && t.logSessionStarted !== undefined && t.logSessionStarted !== '';
}

/** Clé Transloco de la phrase d'un code, ou `null` si le code est inconnu (message serveur alors). */
export function batchFailureKey(code: string): string | null {
  return (TRACKER_BATCH_CODES as readonly string[]).includes(code) ? `trackerBatch.codes.${code}` : null;
}

/** Période proposée par un refus LORA_SLOTS_FULL : le message finit par `nextItv=N`. */
export function loraNextItv(message: string | null | undefined): number | null {
  const m = /nextItv=(\d+)/.exec(message ?? '');
  return m ? Number(m[1]) : null;
}

/** Doublons retirés (ordre gardé), puis paquets de {@link TRACKER_BATCH_MAX}. */
export function chunkTrackerIds(ids: readonly string[], size = TRACKER_BATCH_MAX): string[][] {
  const unique = [...new Set(ids)];
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += size) chunks.push(unique.slice(i, i + size));
  return chunks;
}

export function mergeBatchOutcomes(outcomes: readonly TrackerBatchOutcome[]): TrackerBatchOutcome {
  return {
    succeeded: outcomes.flatMap(o => o.succeeded),
    failed: outcomes.flatMap(o => o.failed),
  };
}

/** « Réessayer les N échecs » : seuls les échecs repartent. */
export function retryableIds(outcome: TrackerBatchOutcome): string[] {
  return outcome.failed.map(f => f.trackerId);
}

/**
 * Manage transfère aussi des gateways, qui n'ont pas de lot serveur : les trackers partent en un
 * lot, les gateways une par une ensuite (`PUT /gateways/{id}/organization`). Un refus gateway sort
 * dans le même récapitulatif, avec le code et le message du serveur.
 */
export function withGateways(
  trackers: BatchExecutor,
  gatewayIds: ReadonlySet<string>,
  transferGateway: (id: string) => Promise<unknown>
): BatchExecutor {
  return async (ids, stopLogging) => {
    const trackerIds = ids.filter(id => !gatewayIds.has(id));
    const outcome: TrackerBatchOutcome = trackerIds.length
      ? await trackers(trackerIds, stopLogging)
      : { succeeded: [], failed: [] };
    const succeeded = [...outcome.succeeded];
    const failed = [...outcome.failed];
    for (const id of ids.filter(i => gatewayIds.has(i))) {
      try {
        await transferGateway(id);
        succeeded.push(id);
      } catch (e) {
        const code = errorCode(e) ?? 'INTERNAL_ERROR';
        failed.push({ trackerId: id, code, message: errorMessage(e, code) });
      }
    }
    return { succeeded, failed };
  };
}
